import React, { ButtonHTMLAttributes, useEffect, useMemo, useState } from 'react';
import useTimeoutFn from 'react-use/lib/useTimeoutFn';
import {
  RefreshButtonWrapper,
  RefreshButtonHelperText,
  StyledRefreshButton,
} from './RefreshButtonStyles';
import { IsFetchingIndicator } from '../IsFetchingIndicator/IsFetchingIndicator';

const FlashMessage = ({ onDestroy }) => {
  const [isReady] = useTimeoutFn(onDestroy, 1000);

  if (!isReady()) {
    return <span>Refresh failed!</span>;
  }

  return null;
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  helperText?: string;
  helperLoadingText?: string;
  isLoading?: boolean;
  errors?: string[];
}

export const RefreshButton: React.FC<Props> = ({
  helperText,
  helperLoadingText = 'Refreshing...',
  children,
  isLoading,
  errors = [],
  ...other
}) => {
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    if (errors.length > 0) {
      setShowErrorMessage(true);
    }
  }, [errors]);

  const content = useMemo(() => {
    if (isLoading) {
      return <IsFetchingIndicator text={helperLoadingText} />;
    }

    if (showErrorMessage) {
      return <FlashMessage onDestroy={() => setShowErrorMessage(false)} />;
    }

    return helperText;
  }, [helperText, isLoading, showErrorMessage]);

  return (
    <RefreshButtonWrapper>
      {(helperText || isLoading) && <RefreshButtonHelperText>{content}</RefreshButtonHelperText>}
      <StyledRefreshButton disabled={isLoading} {...other}>
        {children || 'Refresh'}
      </StyledRefreshButton>
    </RefreshButtonWrapper>
  );
};
